/**
 * pollServices.js — Poll Management data layer.
 *
 * Talks to the polls backend through the shared api/api.js clients, which
 * already attach the base URL, Bearer token, X-Tenant-Code and X-Service-Code
 * from sessionDB. Paths here are relative to HRMS_BASE_URL
 * (`${API_BASE_URL}/saas/hrms`) or ESS_BASE_URL (`${API_BASE_URL}/saas/ess`),
 * matching the controllers' @RequestMapping.
 *
 * Rules that used to live in this file — audience resolution, status
 * transitions, one-vote-per-employee, results aggregation, anonymity — are all
 * enforced server-side now. What remains here is presentation-level reads of
 * fields the server already computed, so the UI never recounts anything and
 * cannot disagree with the database.
 */
import { essGet, essPost, hrmsGet, hrmsPost, hrmsPut } from "../api/api";
import sessionDB from "../utils/sessionDB";
import {
  fetchDepartments as fetchAnnouncementDepartments,
  fetchManagers as fetchAnnouncementManagers,
} from "./announcementServices";

// Service codes must match the @ServiceCode on each handler, or the
// ServiceCodeInterceptor answers 403 naming both codes.
const READ = "HR26S";
const ADD = "HR26A";
const UPDATE = "HR26U";
const RESULTS = "HR26R";
const ESS_READ = "ESS06S";
const ESS_VOTE = "ESS06V";
// Employee directory lookup, shared with the org chart's person popover.
const EMP_LOOKUP = "HR01S";

/** Unwraps the platform envelope: { message, data, timestamp, ... }. */
function unwrap(json) {
  return json?.data ?? json;
}

// ---------------------------------------------------------------------------
// Current user
// ---------------------------------------------------------------------------

/**
 * The signed-in employee, from the session. Only used for presentation
 * (greetings, "is this mine" checks the server has already decided). Every
 * authorisation decision is made server-side from the token.
 */
export function getCurrentUser() {
  const emp = sessionDB.getEmpObj() ?? {};
  const empId = Number(sessionDB.getEmpId()) || 0;
  const name =
    emp.name ??
    emp.employeeName ??
    [emp.firstName, emp.lastName].filter(Boolean).join(" ").trim();
  const roles = (sessionDB.getCanonicalRoleName() || "")
    .split(",")
    .map((r) => r.trim().toLowerCase())
    .filter(Boolean);

  return {
    empId,
    name: name || "You",
    department: emp.departmentName ?? emp.department ?? "",
    roles,
    isHR: roles.some((r) => r === "hr" || r.startsWith("hr ")),
    isManager: roles.some((r) => r.includes("manager")),
    isDirector: roles.some((r) => r.includes("director") || r.includes("management")),
  };
}

// ---------------------------------------------------------------------------
// Server-computed flags
//
// These read fields the API already resolved for this caller. They stay as
// functions so the pages that call effectiveStatus(poll) / hasVoted(poll)
// keep working unchanged.
// ---------------------------------------------------------------------------

/** The server always sends the *effective* status, so this is a plain read. */
export function effectiveStatus(poll) {
  return poll?.status ?? "";
}

export function hasVoted(poll) {
  return Boolean(poll?.hasVoted);
}

/**
 * Whether *this* viewer may open the results screen.
 *
 * HR and the creator always retain access. For everyone else the poll's two
 * visibility switches decide, and the unlock moment differs per switch:
 *
 * <ul>
 *   <li>neither on — results are Creator &amp; HR only, never shown;</li>
 *   <li>resultsAfterVoting — unlocks as soon as this viewer has voted;</li>
 *   <li>shareResults — unlocks only once the poll has closed.</li>
 * </ul>
 *
 * A poll with both on unlocks at whichever comes first. The server enforces the
 * same rule on the results endpoint; the flag it sends is treated as a
 * precondition, not as permission on its own, because a poll that shares
 * nothing must not offer a View Results button at all.
 */
export function canViewResults(poll) {
  if (!poll) return false;
  if (poll.canManage || poll.createdBy?.id === getCurrentUser().empId) return true;
  if (poll.canViewResults === false) return false;

  const afterVoting = Boolean(poll.resultsAfterVoting);
  const afterClose = Boolean(poll.shareResults);

  if (afterVoting && hasVoted(poll)) return true;
  if (afterClose && effectiveStatus(poll) === "Closed") return true;
  return false;
}

export function canManagePolls(poll) {
  return Boolean(poll?.canManage);
}

/**
 * Respondent lists exist only on non-anonymous polls, and only for HR or the
 * creator. The server refuses the request otherwise; this just decides whether
 * to ask.
 */
export function canSeeRespondents(poll, user = getCurrentUser()) {
  if (!poll || poll.anonymous) return false;
  return Boolean(poll.canManage) || poll.createdBy?.id === user.empId;
}

/** A poll only reaches an employee's dashboard if they are in its audience. */
export function isInAudience() {
  return true;
}

export function isFrozen(poll) {
  return effectiveStatus(poll) !== "Draft";
}

// ---------------------------------------------------------------------------
// Reportees and departments
//
// The service code belongs to the endpoint, not to the calling module: the
// interceptor matches it against the @ServiceCode on the handler, so these must
// keep sending the codes announcementServices sends (HR23A / HR02S), not HR26S.
// Explicit wrappers (not re-exports) so webpack always surfaces the named exports.
// ---------------------------------------------------------------------------

export function fetchPollDepartments() {
  return fetchAnnouncementDepartments();
}

export function fetchPollManagers() {
  return fetchAnnouncementManagers();
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** My Polls — what the signed-in user created. */
export async function fetchMyPolls() {
  return unwrap(await hrmsGet("/polls-config", READ)) ?? [];
}

/** HR Poll Log — every poll in the tenant. */
export async function fetchAllPolls() {
  return unwrap(await hrmsGet("/polls-config/all", READ)) ?? [];
}

export async function fetchPollById(pollId) {
  return unwrap(await hrmsGet(`/polls-config/${pollId}`, READ));
}

/**
 * The employee dashboard's two tabs, already split and sorted server-side.
 * There is no Closed tab — a poll that closed without the employee's vote is
 * returned under `completed`.
 */
export async function fetchEmployeePolls() {
  const data = unwrap(await essGet("/emp-polls/dashboard", ESS_READ));
  return {
    active: data?.active ?? [],
    completed: data?.completed ?? [],
  };
}

/** The caller's own submitted response, or null. */
export async function getMyVote(pollId) {
  return unwrap(await essGet(`/emp-polls/${pollId}/my-vote`, ESS_READ)) ?? null;
}

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export async function computeResults(pollId) {
  return unwrap(await hrmsGet(`/polls-results/${pollId}/results`, RESULTS));
}

/**
 * The participants endpoint identifies people by id only, so each row is
 * resolved to a name and department through the shared employee lookup before
 * it reaches the UI. Results are cached for the session and fetched a few at a
 * time — a Global poll can target the whole org, and firing one request per
 * employee at once would stall the browser's connection pool.
 */
const employeeSummaries = new Map();

function fetchEmployeeSummary(empId) {
  if (employeeSummaries.has(empId)) return employeeSummaries.get(empId);

  const pending = hrmsGet(`/emp-info/search/${empId}`, EMP_LOOKUP)
    .then((body) => {
      const emp = unwrap(body) ?? {};
      const name =
        emp.fullName ||
        [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ").trim() ||
        emp.empName ||
        emp.employeeName;
      return {
        name: name || `Employee #${empId}`,
        department: emp.departmentName ?? emp.department ?? "",
      };
    })
    .catch(() => ({ name: `Employee #${empId}`, department: "" }));

  employeeSummaries.set(empId, pending);
  return pending;
}

async function mapWithConcurrency(items, limit, fn) {
  const out = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor++;
      out[index] = await fn(items[index]);
    }
  });
  await Promise.all(workers);
  return out;
}

function withEmployeeNames(rows) {
  return mapWithConcurrency(rows, 6, async (row) => {
    const employeeId = row.employeeId ?? row.empId;
    // Skip the lookup if the API ever starts sending the name itself.
    const person = row.name
      ? { name: row.name, department: row.department ?? "" }
      : await fetchEmployeeSummary(employeeId);
    return { ...row, employeeId, name: person.name, department: person.department };
  });
}

export async function computeParticipants(pollId) {
  const data = unwrap(await hrmsGet(`/polls-results/${pollId}/participants`, RESULTS));
  const [respondents, nonRespondents] = await Promise.all([
    withEmployeeNames(data?.respondents ?? []),
    withEmployeeNames(data?.nonRespondents ?? []),
  ]);
  return { respondents, nonRespondents };
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

/**
 * Maps the Create Poll form state onto the API's PollPayload.
 * Start date is not sent — the server stamps it as "today" at publish.
 * The form carries `endDate` + `endTime`; they combine into an ISO
 * `endDateTime`. Results visibility is the two booleans.
 */
function toPayload(form) {
  // Send the wall-clock the user picked as a naive local datetime
  // ("2026-07-28T15:30:00") — NOT toISOString(), which converts to UTC and
  // appends a "Z". The backend field is a Java LocalDateTime (no timezone):
  // a "Z" string can't be parsed into it, and even if it could, the UTC shift
  // (−5:30 for IST) makes a valid future time look like the past, so the
  // "end date must be in the future" check wrongly rejects it.
  const endDateTime =
    form.endDate && form.endTime ? `${form.endDate}T${form.endTime}:00` : null;
  return {
  pollTitle: form.pollTitle ?? "",
  pollQuestion: form.pollQuestion ?? "",
  description: form.description ?? "",
  responseType: form.responseType || null,
  anonymous: Boolean(form.anonymous),

  audienceGroup: form.audience?.group ?? null,
  audienceManagerId: form.audience?.managerId ?? null,
  audienceDepartmentId: form.audience?.departmentId ?? null,

  endDateTime,
  shareResults: Boolean(form.shareResults),
  resultsAfterVoting: Boolean(form.resultsAfterVoting),

  options: (form.options ?? [])
    .filter(o => (o.text ?? "").trim())
    .map(o => ({
      optionId: Number(o.optionId) || 0,
      text: o.text
    }))
};
}

/**
 * @param {"Draft"|"Published"} status Save as Draft, or publish immediately.
 */
export async function createPoll(form, status = "Draft") {
  const publish = status === "Published";
  return unwrap(await hrmsPost(`/polls-config/add?publish=${publish}`, toPayload(form), ADD));
}

export async function updatePoll(pollId, form, status = "Draft") {
  const publish = status === "Published";
  return unwrap(await hrmsPut(`/polls-config/update/${pollId}?publish=${publish}`, toPayload(form), UPDATE));
}

/** Publishes an existing draft unchanged. */
export async function publishPoll(pollId) {
  return unwrap(await hrmsPost(`/polls-config/${pollId}/publish`, undefined, UPDATE));
}

export async function closePoll(pollId) {
  return unwrap(await hrmsPost(`/polls-config/${pollId}/close`, undefined, UPDATE));
}

/** @param {string} newEndDateTime an ISO date-time string */
export async function extendEndDate(pollId, newEndDateTime) {
  return unwrap(
    await hrmsPost(`/polls-config/${pollId}/extend`, { endDateTime: newEndDateTime }, UPDATE)
  );
}

/**
 * Casts a vote. The server re-validates the window, audience membership and
 * the duplicate rule, so a stale tab gets a 409 rather than a second vote.
 */
export async function submitVote(pollId, response) {
  return unwrap(await essPost(`/emp-polls/${pollId}/vote`, response, ESS_VOTE));
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

/**
 * Triggers a browser download for text already built in the client.
 *
 * <p>The CSV is assembled here rather than fetched from
 * /polls-results/{id}/results.csv because the server lays the summary out
 * down the rows and identifies people by id; the sheet this produces puts
 * every field in its own column and carries the resolved employee names.
 * The BOM keeps Excel from mangling non-ASCII names on open.
 */
export function downloadCsv(filename, csvText) {
  const blob = new Blob([`﻿${csvText}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
